import React, { useEffect, useState, useRef } from "react";
import { Handle, Position, useViewport } from "@xyflow/react";
import { Image, Video, Music, FileText, Loader2, Play, Lightbulb, Grid3X3, ZoomIn, Wand2, Share2, ScrollText, Volume2, VolumeX, Download, History } from "lucide-react";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { downloadFile } from "../../../utils/fileUtils";
import { useDelayedUnmount } from "../../../hooks/useDelayedUnmount";
import AtomEditPanel from "../panels/AtomEditPanel";
import GridEditPanel from "../panels/GridEditPanel";
import ScriptToStoryboardPanel from "../panels/ScriptToStoryboardPanel";
import AiDirectorNode from "./AiDirectorNode";
import { getPublicVideoLabel } from "../../../utils/projectRuntime";
import { getEffectiveAssetMeta } from "../../../utils/guaVideoAssetStatus";
import { normalizeGenerationHistory } from "../utils/generationHistory";

const KIND_META = {
  image: { Icon: Image, label: "图片", color: "var(--color-blue-primary)" },
  video: { Icon: Video, label: "视频", color: "var(--color-green-primary)" },
  audio: { Icon: Music, label: "音频", color: "var(--color-pink-primary)" },
  text:  { Icon: FileText, label: "文本", color: "var(--color-warning)" },
};

// 音频时长格式化：数字秒 → m:ss；字符串原样透传
const formatAudioDuration = (d) => {
  if (d == null) return null;
  if (typeof d === "string") return d;
  if (!Number.isFinite(d)) return null;
  const m = Math.floor(d / 60);
  const s = Math.round(d % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

// 片5 语义缩放阈值：< 0.45 简卡 / 0.45–0.8 紧凑 / > 0.8 完整
const ZOOM_SIMPLE_MAX = 0.45;
const ZOOM_FULL_MIN = 0.8;

const AtomNode = ({ data, id, selected }) => {
  const [hovered, setHovered] = useState(false);
  const [videoSoundOn, setVideoSoundOn] = useState(false);
  const [panelInitialView, setPanelInitialView] = useState("generate");
  // 生成类操作的二次确认（高清放大/宫格拆分都是消耗型操作）
  const [confirmAction, setConfirmAction] = useState(null);
  const expanded = !!data.expanded;

  useEffect(() => {
    if (!expanded) setPanelInitialView("generate");
  }, [expanded]);

  const { mounted: panelVisible } = useDelayedUnmount(expanded, 200);
  const panelRef = useRef(null);
  const { zoom } = useViewport();
  const zoomTier = zoom < ZOOM_SIMPLE_MAX ? "simple" : zoom > ZOOM_FULL_MIN ? "full" : "compact";
  const isSimple = zoomTier === "simple";

  const norm = data.normalized || {};
  const kind = norm.kind || "image";
  const meta = KIND_META[kind] || KIND_META.image;
  const isScriptToStoryboard = data.legacyType === "scriptToStoryboard";
  const isStoryboardShot = data.legacyType === "storyboardShot";
  const Icon = isScriptToStoryboard ? ScrollText : meta.Icon;

  // url 必须是真实的资源链接，排除误取
  // P2-1：generate 节点的最近结果存 data.lastResultUrl（身份不突变，节点仍是生成器）
  const isGenerateNode = data.legacyType === "generate";
  const rawUrl = norm.content?.url || norm.content?.thumbnail
    || data.url || data.lastResultUrl || data.result || data.generatedImage || data.generatedVideo || data.gridImageUrl || data.thumbnail;
  const url = rawUrl && (rawUrl.startsWith('http') || rawUrl.startsWith('/') || rawUrl.startsWith('blob:')) ? rawUrl : null;
  const text = norm.content?.text || data.text || data.description;
  // generate 节点保持生成器身份标题，不随产物变成素材卡
  const title = isGenerateNode
    ? (data.generateType === 'video' ? '生成视频' : '生成图片')
    : (data.name || norm.name || meta.label);
  const status = norm.status || (data.isGenerating ? "generating" : "empty");
  const isVideo = kind === "video";
  const isAudio = kind === "audio";
  const isText = kind === "text";
  const hasContent = !!url || (isText && !!text);
  const generationHistoryCount = isGenerateNode
    ? normalizeGenerationHistory(data).length
    : 0;
  const showGenerationStack = generationHistoryCount > 1 && !expanded && !isSimple;

  const gen = norm.generation || {};
  const aspectRatio = gen.params?.aspectRatio || data.aspectRatio || null;
  const provider = gen.provider || null;
  const prompt = gen.prompt || data.prompt || null;

  const tags = [];
  if (provider) tags.push(getPublicVideoLabel(provider));
  if (aspectRatio) tags.push(aspectRatio);
  if (gen.params?.duration) tags.push(gen.params.duration + "s");

  // 主镜/连接镜徽章（剧本转分镜产物节点）：masterType（位置关系=蓝/戏点=橙）+ purpose（连接=蓝/补全=绿/强调=粉）+ shotSize
  const badges = [];
  if (isStoryboardShot) {
    badges.push({
      text: data.draftStatus === 'reviewed' ? '已复核' : '粗稿',
      color: data.draftStatus === 'reviewed' ? 'var(--color-success)' : 'var(--color-warning)',
    });
  }
  if (data.masterType) {
    badges.push({ text: data.masterType, color: data.masterType === "位置关系" ? "var(--color-blue-primary)" : "var(--color-warning)" });
  }
  if (data.purpose) {
    badges.push({ text: data.purpose, color: data.purpose === "补全" ? "var(--color-green-primary)" : data.purpose === "强调" ? "var(--color-pink-primary)" : "var(--color-blue-primary)" });
  }
  if (data.shotSize) {
    badges.push({ text: data.shotSize, color: "var(--color-text-muted)" });
  }
  if (isStoryboardShot && data.screenDirection && data.screenDirection !== '不适用') {
    badges.push({ text: data.screenDirection, color: 'var(--color-blue-primary)' });
  }
  if (isStoryboardShot && data.axisStatus) {
    badges.push({
      text: data.axisStatus,
      color: data.axisStatus === '稳定'
        ? 'var(--color-success)'
        : data.axisStatus === '有意越轴'
          ? 'var(--color-blue-primary)'
          : 'var(--color-danger)',
    });
  }

  // 入库状态：已入库（Active）→ 绿色外框 + 已入库徽章（视频生成时用 assetId 引用）
  const ingestMeta = getEffectiveAssetMeta(data, data.assetProvider);
  const ingestActive = ingestMeta.status === 'Active';
  const ingestProcessing = !ingestActive && ingestMeta.status === 'Processing';
  if (ingestActive) {
    badges.push({ text: "已入库", color: "var(--color-success)" });
  } else if (ingestProcessing) {
    badges.push({ text: "入库中", color: "var(--color-warning)" });
  }

  // 点击由 ReactFlow onNodeClick 统一处理，AtomNode 不拦截

  // 片5 AI 导演对话节点：独立对话卡片（消息区 + 输入框），不走 atom 媒体卡渲染
  if (data.legacyType === "aiDirector") {
    return <AiDirectorNode data={data} id={id} selected={selected} />;
  }

  return (
    <div
      className={`relative atom-node atom-zoom-${zoomTier}`}
      style={{
        width: expanded ? (isStoryboardShot ? 440 : 320) : 280,
        transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 200ms cubic-bezier(0.22, 1, 0.36, 1)",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* 连接点 — 在最外层，不受卡片 overflow:hidden 影响 */}
      <Handle type="target" position={Position.Left} id="left" style={{
        width: 20, height: 20,
        background: hovered ? "var(--color-bg-tertiary)" : "transparent",
        border: hovered ? "2px solid var(--color-border-primary)" : "2px solid transparent",
        borderRadius: "50%",
        cursor: "crosshair",
        transition: "all 0.2s ease",
      }} />
      <Handle type="source" position={Position.Right} id="right" style={{
        width: 20, height: 20,
        background: hovered ? "var(--color-bg-tertiary)" : "transparent",
        border: hovered ? "2px solid var(--color-border-primary)" : "2px solid transparent",
        borderRadius: "50%",
        cursor: "crosshair",
        transition: "all 0.2s ease",
      }} />

      {/* ── 卡片外：标题栏（简卡模式下即缩略信息：图标 + 标题 + 类型徽章） ── */}
      <div className="flex items-center justify-between px-0.5 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <Icon size={12} style={{ color: "var(--color-text-muted)" }} />
          <span className="text-xs truncate" style={{ color: "var(--color-text-secondary)", maxWidth: 180 }}>
            {title}
          </span>
        </div>
        {/* 类型小徽章：仅简卡模式显示（CSS 控制） */}
        <span className="atom-simple-badge items-center px-1.5 py-0.5 rounded text-2xs font-medium" style={{
          background: `color-mix(in srgb, ${meta.color} 14%, transparent)`,
          color: meta.color,
          border: `1px solid color-mix(in srgb, ${meta.color} 32%, transparent)`,
        }}>
          {isScriptToStoryboard ? "剧本" : meta.label}
        </span>
        {aspectRatio && (
          <span className="atom-detail text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>
            {aspectRatio}
          </span>
        )}
      </div>

      {/* ── 卡片 ── */}
      <div
        className="relative"
        style={{
          isolation: "isolate",
          marginRight: showGenerationStack ? 14 : 0,
          marginBottom: showGenerationStack ? 14 : 0,
          transform: hovered && !expanded ? "translateY(-1px)" : "none",
          transition: "transform 0.2s ease",
        }}
      >
        {showGenerationStack && (
          <>
            <div
              aria-hidden="true"
              className="absolute inset-0 rounded-[10px]"
              style={{
                zIndex: 0,
                pointerEvents: "none",
                transform: "translate3d(13px, 13px, 0)",
                background: "color-mix(in srgb, var(--color-bg-secondary) 42%, transparent)",
                border: "1px solid color-mix(in srgb, var(--color-border-primary) 68%, transparent)",
                backdropFilter: "blur(6px) saturate(1.15)",
                WebkitBackdropFilter: "blur(6px) saturate(1.15)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 26px -18px rgba(0,0,0,0.72)",
              }}
            />
            <div
              aria-hidden="true"
              className="absolute inset-0 rounded-[10px]"
              style={{
                zIndex: 1,
                pointerEvents: "none",
                transform: "translate3d(7px, 7px, 0)",
                background: "color-mix(in srgb, var(--color-bg-tertiary) 58%, transparent)",
                border: "1px solid color-mix(in srgb, var(--color-border-primary) 82%, transparent)",
                backdropFilter: "blur(10px) saturate(1.25)",
                WebkitBackdropFilter: "blur(10px) saturate(1.25)",
                boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 10px 24px -16px rgba(0,0,0,0.65)",
              }}
            />
          </>
        )}
        <div
          className={`relative${status === "generating" ? " canvas-node-card--generating" : ""}`}
          style={{
            zIndex: 2,
            borderRadius: 10,
            overflow: "hidden",
            border: selected
              ? "2px solid var(--color-accent-primary)"
              : ingestActive
                ? "2px solid var(--color-success)"
                : hovered
                  ? "1px solid var(--color-border-primary)"
                  : "1px solid var(--color-border-secondary)",
            // P4 多层阴影系统：inset 高光 + 近景深影 + 远景环境影；选中加 accent 外环
            boxShadow: selected
              ? "0 0 0 3px var(--color-accent-light), inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35)"
              : hovered
                ? "inset 0 1px 0 rgba(255,255,255,0.05), 0 20px 36px -10px rgba(0,0,0,0.55), 0 44px 80px -24px rgba(0,0,0,0.4)"
                : "inset 0 1px 0 rgba(255,255,255,0.05), 0 12px 24px -8px rgba(0,0,0,0.5), 0 32px 64px -24px rgba(0,0,0,0.35)",
            transition: "border 0.2s ease, box-shadow 0.3s ease",
            background: "var(--color-bg-secondary)",
            cursor: expanded ? "default" : "pointer",
          }}
        >
        {/* 顶部功能条 — hover 时浮出（简卡模式隐藏） */}
        {hovered && hasContent && !expanded && !isSimple && (
          <div
            className="absolute top-0 left-0 right-0 z-20 flex items-center gap-0.5 px-2 py-1.5"
            style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)" }}
            onClick={(e) => e.stopPropagation()}
          >
            {!isText && !isAudio && (
              <>
                <button
                  className="nodrag p-1 rounded transition-all hover:bg-white/10"
                  title="打光"
                  onClick={() => data.onToggleExpand?.(id)}
                >
                  <Lightbulb size={12} style={{ color: "var(--color-text-secondary)" }} />
                </button>
                <button
                  className="nodrag p-1 rounded transition-all hover:bg-white/10"
                  title="九宫格拆分"
                  onClick={() => setConfirmAction('splitGrid')}
                >
                  <Grid3X3 size={12} style={{ color: "var(--color-text-secondary)" }} />
                </button>
                <button
                  className="nodrag p-1 rounded transition-all hover:bg-white/10"
                  title="高清放大（消耗一次生成）"
                  onClick={() => setConfirmAction('upscale')}
                >
                  <ZoomIn size={12} style={{ color: "var(--color-text-secondary)" }} />
                </button>
                {/* 下载：图片/视频节点通用，hover 提示 */}
                <button
                  className="nodrag p-1 rounded transition-all hover:bg-white/10"
                  title={isVideo ? "下载视频" : "下载图片"}
                  onClick={() => {
                    const ext = isVideo ? 'mp4' : 'png';
                    const base = (title || '画布素材').replace(/[\\/:*?"<>|]/g, '_');
                    try {
                      downloadFile(url, `${base}.${ext}`);
                    } catch {
                      window.open(url, '_blank', 'noopener');
                    }
                  }}
                >
                  <Download size={12} style={{ color: "var(--color-text-secondary)" }} />
                </button>
                {isGenerateNode && generationHistoryCount > 0 && (
                  <button
                    type="button"
                    className="nodrag nopan p-1 rounded transition-all hover:bg-white/10"
                    title={`生成历史（${generationHistoryCount}）`}
                    aria-label={`展开生成历史，共 ${generationHistoryCount} 个版本`}
                    onClick={() => {
                      setPanelInitialView("history");
                      data.onToggleExpand?.(id);
                    }}
                  >
                    <History size={12} style={{ color: "var(--color-text-secondary)" }} />
                  </button>
                )}
                {/* P2-1 就地更新：generate 节点按需导出最近结果为独立素材节点（spawn 幂等查重） */}
                {isGenerateNode && data.lastResultUrl && (
                  <button
                    className="nodrag p-1 rounded transition-all hover:bg-white/10"
                    title="导出为素材节点"
                    onClick={() => data.onExportResult?.(id)}
                  >
                    <Share2 size={12} style={{ color: "var(--color-text-secondary)" }} />
                  </button>
                )}
                <div className="flex-1" />
                <button
                  className="nodrag p-1 rounded transition-all hover:bg-white/10"
                  title="重新生成"
                  onClick={() => data.onGenerate?.(id, data)}
                >
                  <Wand2 size={12} style={{ color: "var(--color-text-secondary)" }} />
                </button>
              </>
            )}
          </div>
        )}

        {/* 生成中遮罩 */}
        {status === "generating" && (
          <div className="absolute inset-0 flex items-center justify-center z-10" style={{
            background: "var(--overlay-medium)",
            animation: "pulse 2s ease-in-out infinite",
          }}>
            <Loader2 size={24} className="animate-spin" style={{ color: "var(--color-text-secondary)" }} />
          </div>
        )}

        {/* P2-1 generate 节点角标：缩略图区可显示最近结果，但保留生成器身份 */}
        {isGenerateNode && hasContent && (
          <div className="absolute top-2 left-2 z-10 flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: "var(--overlay-medium)" }}>
            <Wand2 size={9} style={{ color: "var(--color-accent-primary)" }} />
            <span className="text-2xs" style={{ color: "var(--color-accent-primary)" }}>生成</span>
          </div>
        )}

        {/* 收起时直接在节点上显示版本入口；独立毛玻璃后层形成叠放效果。 */}
        {showGenerationStack && (
          <button
            type="button"
            className="nodrag nopan absolute bottom-2 left-2 z-20 flex items-center gap-1 rounded-full px-2 py-1 text-2xs font-medium cursor-pointer transition-colors"
            style={{
              color: "#fff",
              background: "var(--overlay-medium)",
              border: "1px solid rgba(255, 255, 255, 0.2)",
            }}
            onClick={(event) => {
              event.stopPropagation();
              setPanelInitialView("history");
              data.onToggleExpand?.(id);
            }}
            title="展开生成历史"
            aria-label={`展开生成历史，共 ${generationHistoryCount} 个版本`}
          >
            <History size={10} aria-hidden="true" />
            历史 {generationHistoryCount}
          </button>
        )}

        {/* 内容 */}
        {data.uploading ? (
          /* 上传占位：loading 骨架（本地上传创建，成功就地转正 / 失败移除） */
          <div className="atom-media flex flex-col items-center justify-center gap-2 py-10" style={{ minHeight: 120 }}>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)" }}>
              <Loader2 size={22} className="animate-spin" style={{ color: "var(--color-accent-primary)" }} />
            </div>
            <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>上传中...</span>
            {data.name && (
              <span className="text-2xs truncate" style={{ color: "var(--color-text-muted)", maxWidth: 200 }} title={data.name}>
                {data.name}
              </span>
            )}
          </div>
        ) : hasContent ? (
          isVideo ? (
            <div className="atom-media relative"
              style={{ minHeight: 160, background: "#000" }}
              onMouseEnter={(e) => { const v = e.currentTarget.querySelector('video'); v?.play(); }}
              onMouseLeave={(e) => { const v = e.currentTarget.querySelector('video'); if (v) { v.pause(); v.currentTime = 0; } }}
            >
              <video
                src={url}
                poster={data.thumbnail || norm.content?.thumbnail || ""}
                className="w-full block"
                style={{ minHeight: 160 }}
                muted={!videoSoundOn}
                playsInline
                preload="none"
              />
              <div className="absolute top-2 right-2 flex items-center gap-1 px-1.5 py-0.5 rounded" style={{ background: "var(--overlay-medium)" }}>
                <Play size={9} fill="white" style={{ color: "white" }} />
                {norm.content?.duration && (
                  <span className="text-2xs font-mono-num" style={{ color: "#fff" }}>{norm.content.duration}s</span>
                )}
              </div>
              {/* 声音开关：悬停预览默认静音，点击（真实用户手势）后带声播放 */}
              <button
                type="button"
                className="nodrag absolute bottom-2 right-2 z-10 flex items-center justify-center rounded p-1 transition-colors"
                style={{ background: "var(--overlay-medium)", color: videoSoundOn ? "var(--color-accent-primary)" : "#fff" }}
                title={videoSoundOn ? "静音" : "打开声音"}
                onClick={(e) => {
                  e.stopPropagation();
                  const v = e.currentTarget.closest('.atom-media')?.querySelector('video');
                  const next = !videoSoundOn;
                  setVideoSoundOn(next);
                  if (v) {
                    v.muted = !next;
                    if (next) v.play().catch(() => {});
                  }
                }}
              >
                {videoSoundOn ? <Volume2 size={12} /> : <VolumeX size={12} />}
              </button>
            </div>
          ) : isAudio ? (
            /* 片3 音频完整卡片：图标 + 名称 + 时长（若有）+ styled 原生播放条 */
            <div className="atom-media atom-audio flex flex-col gap-2 px-3 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{
                    background: `color-mix(in srgb, ${meta.color} 14%, transparent)`,
                    border: `1px solid color-mix(in srgb, ${meta.color} 30%, transparent)`,
                  }}
                >
                  <Music size={14} style={{ color: meta.color }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-xs truncate" style={{ color: "var(--color-text-primary)" }} title={title}>
                    {title}
                  </div>
                  {formatAudioDuration(norm.content?.duration ?? data.duration) && (
                    <div className="text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>
                      {formatAudioDuration(norm.content?.duration ?? data.duration)}
                    </div>
                  )}
                </div>
              </div>
              <audio src={url} controls preload="metadata" className="nodrag atom-audio-player" />
            </div>
          ) : isText ? (
            <div className="atom-media px-3 py-3" style={{ minHeight: 60 }}>
              <p className="text-xs leading-relaxed" style={{ color: "var(--color-text-secondary)" }}>{text}</p>
            </div>
          ) : (
            <img src={url} alt="" className="w-full block" />
          )
        ) : isScriptToStoryboard ? (
          /* 剧本转分镜节点卡片：显示剧本摘要或引导文案 */
          <div className="px-3 py-3" style={{ minHeight: 60 }}>
            {data.scriptText ? (
              <p className="text-xs leading-relaxed line-clamp-4" style={{ color: "var(--color-text-secondary)" }}>
                {data.scriptText}
              </p>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 py-6">
                <ScrollText size={24} style={{ color: "var(--color-text-muted)", opacity: 0.4 }} />
                <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>点击展开，选择剧本来源</span>
              </div>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-2 py-10">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: "var(--color-bg-tertiary)" }}>
              <Icon size={24} style={{ color: "var(--color-text-muted)", opacity: 0.4 }} />
            </div>
            <span className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              {isText ? "描述" : meta.label}
            </span>
          </div>
        )}
        </div>
      </div>

      {/* ── 卡片外：底部标签（收起时显示，带淡入淡出；简卡模式 CSS 隐藏） ── */}
      <div className="atom-detail" style={{
        opacity: !expanded && (tags.length > 0 || prompt || badges.length > 0) ? 1 : 0,
        maxHeight: !expanded && (tags.length > 0 || prompt || badges.length > 0) ? 30 : 0,
        overflow: "hidden",
        transition: "opacity 0.2s ease, max-height 0.25s ease",
      }}>
        <div className="flex items-center gap-1.5 px-0.5 mt-1.5 flex-wrap">
          {badges.map((badge, i) => (
            <span
              key={`badge-${i}`}
              className="text-2xs px-1.5 py-0.5 rounded font-medium"
              style={{
                background: `color-mix(in srgb, ${badge.color} 15%, transparent)`,
                color: badge.color,
                border: `1px solid color-mix(in srgb, ${badge.color} 35%, transparent)`,
              }}
            >
              {badge.text}
            </span>
          ))}
          {tags.map((tag, i) => (
            <span key={i} className="text-2xs px-1.5 py-0.5 rounded" style={{ background: "var(--color-bg-tertiary)", color: "var(--color-text-muted)" }}>
              {tag}
            </span>
          ))}
          {prompt && (
            <span className="text-2xs truncate flex-1 min-w-0" style={{ color: "var(--color-text-muted)", maxWidth: 180 }} title={prompt}>
              {prompt}
            </span>
          )}
        </div>
      </div>

      {/* ── 完整版（zoom > 0.8）：描述摘要行 ── */}
      {zoomTier === "full" && !expanded && data.description && (
        <div className="px-0.5 mt-1 text-2xs leading-relaxed line-clamp-2" style={{ color: "var(--color-text-muted)" }} title={data.description}>
          {data.description}
        </div>
      )}

      {/* ── 编辑面板（展开时滑入，根据 legacyType 选择面板；简卡模式 CSS 隐藏） ── */}
      {panelVisible && (
        <div
          ref={panelRef}
          className="atom-detail"
          style={{
            width: isGenerateNode ? 640 : "100%",
            marginLeft: isGenerateNode ? "calc((100% - 640px) / 2)" : 0,
            opacity: expanded ? 1 : 0,
            transform: expanded ? "translateY(0)" : "translateY(-8px)",
            maxHeight: expanded ? (isStoryboardShot ? 760 : isGenerateNode ? 780 : 600) : 0,
            overflowX: "hidden",
            overflowY: expanded && (isStoryboardShot || isGenerateNode) ? "auto" : "hidden",
            transition: "opacity 0.2s ease, transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), max-height 0.3s ease",
          }}
        >
          {data.legacyType === "storyboardGrid" ? (
            <GridEditPanel data={data} id={id} onClose={() => data.onToggleExpand?.(id)} />
          ) : data.legacyType === "scriptToStoryboard" ? (
            <ScriptToStoryboardPanel data={data} id={id} onClose={() => data.onToggleExpand?.(id)} />
          ) : (
            <AtomEditPanel
              data={data}
              id={id}
              kind={kind}
              initialView={panelInitialView}
              onClose={() => data.onToggleExpand?.(id)}
            />
          )}
        </div>
      )}
      {/* 生成类操作二次确认弹窗 */}
      <ConfirmDialog
        isOpen={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        onConfirm={() => {
          if (confirmAction === 'upscale') {
            data.onGenerate?.(id, { ...data, upscale: true });
          } else if (confirmAction === 'splitGrid') {
            data.onSplitGrid?.(id, { gridImageUrl: url, rows: 3, cols: 3 });
          }
        }}
        title={confirmAction === 'upscale' ? "确认高清放大" : "确认宫格拆分"}
        message={confirmAction === 'upscale'
          ? "将对这张图片进行高清放大，会消耗一次生成费用，确认继续？"
          : "将把这张图按 3×3 宫格拆分为多张图片，确认继续？"}
        confirmLabel="确认生成"
        cancelLabel="取消"
        variant="primary"
      />
    </div>
  );
};

export default React.memo(AtomNode);
